import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom';
import { CONSOLE_BUILD_ID, WITNESS_ENV } from '../../lib/console-env';
import { configurePlatformAuth, hasActivePlatformSession } from '../../lib/platform-auth';

interface PlatformSection {
  readonly to: string;
  readonly label: string;
}

/**
 * Primary platform navigation. It remains visible while drilling into a
 * tenant-management view so platform scope never looks like a tenant launcher.
 */
const PLATFORM_SECTIONS: readonly PlatformSection[] = [
  { to: '/platform', label: 'Overview' },
  { to: '/platform/tenants', label: 'Tenants' },
  { to: '/platform/users', label: 'Users' },
  { to: '/platform/usage', label: 'Usage & spend' },
  { to: '/platform/locations', label: 'Location index' },
  { to: '/platform/templates', label: 'Template library' },
];

export function PlatformLayout() {
  const [sessionState, setSessionState] = useState<'checking' | 'active' | 'missing'>('checking');
  useEffect(() => {
    configurePlatformAuth()
      .then(hasActivePlatformSession)
      .then((active) => setSessionState(active ? 'active' : 'missing'))
      .catch(() => setSessionState('missing'));
  }, []);

  if (sessionState === 'checking') {
    return <main className="mx-auto max-w-md px-6 py-20">Checking sign-in…</main>;
  }
  if (sessionState === 'missing') return <Navigate to="/platform/login" replace />;

  const envBadgeClass =
    WITNESS_ENV === 'prod'
      ? 'bg-neutral-900 text-neutral-50'
      : 'bg-amber-500 text-amber-950';

  return (
    <div className="min-h-screen">
      <header className="border-b border-neutral-200 bg-neutral-50">
        <div className="mx-auto flex max-w-6xl items-center px-6 py-3">
          <div className="flex items-center gap-3">
            <span
              className={`rounded px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${envBadgeClass}`}
            >
              {WITNESS_ENV}
            </span>
            <span className="rounded bg-neutral-900 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-neutral-50">
              Platform
            </span>
            <Link
              to="/"
              className="font-serif text-lg tracking-tight text-neutral-900"
            >
              Witness console
            </Link>
          </div>
        </div>
      </header>
      <nav className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-6">
          {PLATFORM_SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.to === '/platform'}
              className={({ isActive }) =>
                `whitespace-nowrap border-b-2 px-3 py-3 text-sm transition-colors ${
                  isActive
                    ? 'border-neutral-900 font-medium text-neutral-900'
                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                }`
              }
            >
              {section.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <main className="mx-auto max-w-6xl px-6 py-8">
        <Outlet />
      </main>
      <footer className="mx-auto max-w-6xl px-6 py-6 font-mono text-xs text-neutral-500">
        build {CONSOLE_BUILD_ID}
      </footer>
    </div>
  );
}
